import AccessAlarmOutlined from '@mui/icons-material/AccessAlarmOutlined';
import AccessTimeOutlined from '@mui/icons-material/AccessTimeOutlined';
import BlockOutlined from '@mui/icons-material/BlockOutlined';
import CreditCardOutlined from '@mui/icons-material/CreditCardOutlined';
import ThumbUpOutlined from '@mui/icons-material/ThumbUpOutlined';
import { useTheme } from '@mui/material';
import useTranslation from 'hooks/useTranslation/useTranslation';
import { useMemo } from 'react';
import { useNavigate } from 'react-router';
import { filterStatusDefault } from 'v2/filters/accessCards/cardFilterConstants';
import { FilterValidity } from 'v2/filters/accessCards/cardFilterTypes';
import { serializeBooleanRecordToParam, serializeEnum } from 'v2/filters/filterUrlParams';
import { RowsProps } from 'v2/pages/Dashboard/cards/Card';
import { getSeverityColors } from 'v2/pages/Dashboard/hooks/getSeverityColors';
import { CARDS_ACCESS_CARDS_PATH } from 'v2/paths';
import { formatCountLabel } from 'v2/utils/formatCountLabel';

interface Props {
  cardsExpired: number;
  cardsRightsChanged: number;
  cardsBlocked: number;
  cardsExpiringSoon: number;
}

type CardStatus = 'expired' | 'rightsChanged' | 'expiringSoon' | 'blocked';

const CARD_ORDER: Record<CardStatus, number> = {
  expired: 0,
  rightsChanged: 1,
  expiringSoon: 2,
  blocked: 3
};

const useCardsRows = ({ cardsExpired, cardsRightsChanged, cardsBlocked, cardsExpiringSoon }: Props): RowsProps[] => {
  const { translation } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();

  return useMemo(() => {
    const severityColors = getSeverityColors(theme);

    type CardRow = Pick<RowsProps, 'Icon' | 'text'> & {
      count: number;
      severity: 'error' | 'warning' | 'secondary';
      status: CardStatus;
      onClick?: () => void;
    };

    const rows = (
      [
        {
          count: cardsExpired,
          text: formatCountLabel(
            cardsExpired,
            translation.dashboard_cards_expired_singular,
            translation.dashboard_cards_expired_plural
          ),
          Icon: AccessTimeOutlined,
          severity: 'error',
          status: 'expired',
          onClick: () => {
            const status = serializeBooleanRecordToParam({ ...filterStatusDefault, cardExpired: true });
            navigate(`${CARDS_ACCESS_CARDS_PATH}?status=${status}`);
          }
        },
        {
          count: cardsRightsChanged,
          text: formatCountLabel(
            cardsRightsChanged,
            translation.dashboard_cards_rights_changed_singular,
            translation.dashboard_cards_rights_changed_plural
          ),
          Icon: CreditCardOutlined,
          severity: 'error',
          status: 'rightsChanged',
          onClick: () => {
            const status = serializeBooleanRecordToParam({ ...filterStatusDefault, accessRightsChanged: true });
            navigate(`${CARDS_ACCESS_CARDS_PATH}?status=${status}`);
          }
        },
        {
          count: cardsBlocked,
          text: formatCountLabel(
            cardsBlocked,
            translation.dashboard_cards_blocked_singular,
            translation.dashboard_cards_blocked_plural
          ),
          Icon: BlockOutlined,
          severity: 'secondary',
          status: 'blocked',
          onClick: () => {
            const status = serializeBooleanRecordToParam({ ...filterStatusDefault, cardBlocked: true });
            navigate(`${CARDS_ACCESS_CARDS_PATH}?status=${status}`);
          }
        },
        {
          count: cardsExpiringSoon,
          text: formatCountLabel(
            cardsExpiringSoon,
            translation.dashboard_cards_expiring_soon_singular,
            translation.dashboard_cards_expiring_soon_plural
          ),
          Icon: AccessAlarmOutlined,
          severity: 'warning',
          status: 'expiringSoon',
          onClick: () => navigate(`${CARDS_ACCESS_CARDS_PATH}?validity=${serializeEnum(FilterValidity.OneWeek)}`)
        }
      ] satisfies CardRow[]
    ).filter((row) => row.count > 0);

    const sortedRows = [...rows].sort((a, b) => CARD_ORDER[a.status] - CARD_ORDER[b.status]);

    if (sortedRows.length === 0) {
      return [
        {
          Icon: ThumbUpOutlined,
          text: translation.all_cards_are_up_to_date,
          onClick: () => navigate(CARDS_ACCESS_CARDS_PATH),
          ...severityColors.success
        }
      ];
    }

    return sortedRows.map((row) => ({
      Icon: row.Icon,
      text: row.text,
      onClick: row.onClick,
      ...severityColors[row.severity],
      ...(row.status === 'blocked' && {
        textColor: theme.palette.text.primary,
        iconColor: theme.custom.action.active
      })
    }));
  }, [cardsExpired, cardsRightsChanged, cardsBlocked, cardsExpiringSoon, translation, theme, navigate]);
};

export default useCardsRows;
